import { Routes, Route, Navigate } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import { useEffect } from 'react';
import TimelinePage from './pages/TimelinePage';
import LocationTimelinePage from './pages/LocationTimelinePage';
import VerificationPage from './pages/VerificationPage';
import OperationalReplayPage from './pages/OperationalReplayPage';
import HelpPage from './pages/HelpPage';
import MapPage from './pages/MapPage';
import AuthTestPage from './pages/AuthTestPage';
import UsersPage from './pages/admin/UsersPage';
import OrganizationsPage from './pages/admin/OrganizationsPage';
import OrganizationDetailPage from './pages/admin/OrganizationDetailPage';
import UserDetailPage from './pages/admin/UserDetailPage';
import LocationGroupsPage from './pages/admin/LocationGroupsPage';
import LocationGroupDetailPage from './pages/admin/LocationGroupDetailPage';
import FeatureFlagsPage from './pages/admin/FeatureFlagsPage';
import FeatureFlagDetailPage from './pages/admin/FeatureFlagDetailPage';
import FeedbackAdminPage from './pages/admin/FeedbackPage';
import AuditLogPage from './pages/admin/AuditLogPage';
import { Layout } from './components';
import { AuthenticationGuard, CallbackHandler, SuperAdminGuard, AdminGuard } from './components/auth';
import * as Sentry from '@sentry/react';

// Create a Sentry error boundary with fallback component
const SentryErrorBoundary = ({ children }: { children: React.ReactNode }) => (
  <Sentry.ErrorBoundary
    fallback={(errorData) => (
      <Box
        sx={{
          p: 3,
          m: 2,
          bgcolor: '#f8d7da',
          color: '#721c24',
          borderRadius: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2
        }}
      >
        <Typography variant="h5" component="h2">
          Something went wrong
        </Typography>
        <Typography>
          We've been notified about this issue and we'll take a look at it shortly.
        </Typography>
        <Typography sx={{ mt: 2, p: 2, bgcolor: 'rgba(0,0,0,0.05)', borderRadius: 1 }}>
          Error: {errorData.error?.toString() || 'An unknown error occurred'}
        </Typography>
        {errorData.eventId && (
          <Typography variant="caption" sx={{ opacity: 0.7 }}>
            Event ID: {errorData.eventId}
          </Typography>
        )}
        <button 
          onClick={errorData.resetError}
          style={{
            padding: '8px 16px',
            backgroundColor: '#721c24',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            marginTop: '10px'
          }}
        >
          Try again
        </button>
      </Box>
    )}
    showDialog={false}
  >
    {children}
  </Sentry.ErrorBoundary>
);

function App() {

  useEffect(() => {
    const env = import.meta.env.VITE_APP_ENV;
    let title = 'FogCast';
    if (env && env !== 'production') {
      title += ` - ${env}`;
    }
    document.title = title;
  }, []);

  return (
    <SentryErrorBoundary>
      <Routes>
        <Route path="/callback" element={<CallbackHandler />} />
        <Route path="/" element={
          <AuthenticationGuard>
            <Layout>
              <TimelinePage />
            </Layout>
          </AuthenticationGuard>
        } />
        <Route path="/help" element={
          <AuthenticationGuard>
            <Layout>
              <HelpPage />
            </Layout>
          </AuthenticationGuard>
        } />
        <Route path="/map" element={
          <AuthenticationGuard>
            <Layout>
              <MapPage />
            </Layout>
          </AuthenticationGuard>
        } />
        <Route path="/location/:idOrIcao" element={
          <AuthenticationGuard>
            <Layout>
              <LocationTimelinePage />
            </Layout>
          </AuthenticationGuard>
        } />
        <Route path="/verification/:idOrIcao" element={
          <AuthenticationGuard>
            <SuperAdminGuard>
              <Layout>
                <VerificationPage />
              </Layout>
            </SuperAdminGuard>
          </AuthenticationGuard>
        } />
        <Route path="/operational-replay/:idOrIcao" element={
          <AuthenticationGuard>
            <SuperAdminGuard>
              <Layout>
                <OperationalReplayPage />
              </Layout>
            </SuperAdminGuard>
          </AuthenticationGuard>
        } />
        <Route path="/auth-test" element={
          <AuthenticationGuard>
            <Layout>
              <AuthTestPage />
            </Layout>
          </AuthenticationGuard>
        } />
        <Route path="/admin/users" element={
          <AuthenticationGuard>
            <AdminGuard>
              <Layout>
                <UsersPage />
              </Layout>
            </AdminGuard>
          </AuthenticationGuard>
        } />
        <Route path="/admin/users/:uuid" element={
          <AuthenticationGuard>
            <AdminGuard>
              <Layout>
                <UserDetailPage />
              </Layout>
            </AdminGuard>
          </AuthenticationGuard>
        } />
        <Route path="/admin/organizations" element={
          <AuthenticationGuard>
            <SuperAdminGuard>
              <Layout>
                <OrganizationsPage />
              </Layout>
            </SuperAdminGuard>
          </AuthenticationGuard>
        } />
        <Route path="/admin/organizations/:id" element={
          <AuthenticationGuard>
            <SuperAdminGuard>
              <Layout>
                <OrganizationDetailPage />
              </Layout>
            </SuperAdminGuard>
          </AuthenticationGuard>
        } />
        <Route path="/admin/location-groups" element={
          <AuthenticationGuard>
            <SuperAdminGuard>
              <Layout>
                <LocationGroupsPage />
              </Layout>
            </SuperAdminGuard>
          </AuthenticationGuard>
        } />
        <Route path="/admin/location-groups/:id" element={
          <AuthenticationGuard>
            <SuperAdminGuard>
              <Layout>
                <LocationGroupDetailPage />
              </Layout>
            </SuperAdminGuard>
          </AuthenticationGuard>
        } />
        <Route path="/admin/feature-flags" element={
          <AuthenticationGuard>
            <SuperAdminGuard>
              <Layout>
                <FeatureFlagsPage />
              </Layout>
            </SuperAdminGuard>
          </AuthenticationGuard>
        } />
        <Route path="/admin/feature-flags/:id" element={
          <AuthenticationGuard>
            <SuperAdminGuard>
              <Layout>
                <FeatureFlagDetailPage />
              </Layout>
            </SuperAdminGuard>
          </AuthenticationGuard>
        } />
        <Route path="/admin/audit-log" element={
          <AuthenticationGuard>
            <SuperAdminGuard>
              <Layout>
                <AuditLogPage />
              </Layout>
            </SuperAdminGuard>
          </AuthenticationGuard>
        } />
        <Route path="/admin/feedback" element={
          <AuthenticationGuard>
            <SuperAdminGuard>
              <Layout>
                <FeedbackAdminPage />
              </Layout>
            </SuperAdminGuard>
          </AuthenticationGuard>
        } />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </SentryErrorBoundary>
  );
}

export default App; 